import { cn } from '@/lib/cn';

// ── Friendly Result mapping ──────────────────────────────────────────────────
//
// hangup_cause is raw — values like 'caller_hangup', 'NORMAL_CLEARING',
// 'handler_error', etc. We map them to one of four buckets the customer
// can actually read. Unknown values fall through to the raw text so an
// engineer debugging a new case still has something to grep for.
//
// Shared between the CallLogList table cell and the CallSessionDrawer
// detail card so the two views stay visually consistent.

export type ResultTone = 'normal' | 'transfer' | 'warn' | 'error' | 'muted';

export function friendlyResult(
  hangupCause: string | null,
): { label: string; tone: ResultTone } {
  if (!hangupCause) return { label: '—', tone: 'muted' };
  const c = hangupCause.toLowerCase();
  if (
    c === 'caller_hangup'
    || c === 'socket_closed'
    || c === 'normal_clearing'
    || c === 'normal_end'
    || c === 'tree_end'
    || c.endsWith('_hangup')
  ) return { label: 'Caller hung up', tone: 'normal' };
  if (c.startsWith('transfer')) return { label: 'Transferred', tone: 'transfer' };
  if (c === 'auth_denied' || c === 'auth_list_denied') {
    return { label: 'Authorization denied', tone: 'warn' };
  }
  if (
    c === 'handler_error'
    || c === 'runtime_error'
    || c === 'node_missing'
    || c === 'node_inactive'
    || c === 'unsupported_node_type'
    || c === 'max_steps_exceeded'
  ) return { label: 'System error', tone: 'error' };
  return { label: hangupCause, tone: 'muted' };
}

const TONE_CLASSES: Record<ResultTone, string> = {
  normal: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  transfer: 'bg-blue-50 border-blue-200 text-blue-700',
  warn: 'bg-amber-50 border-amber-200 text-amber-700',
  error: 'bg-rose-50 border-rose-200 text-rose-700',
  muted: 'bg-slate-50 border-slate-200 text-slate-600',
};

export function ResultChip({ hangupCause }: { hangupCause: string | null }) {
  const { label, tone } = friendlyResult(hangupCause);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
        TONE_CLASSES[tone],
      )}
      title={hangupCause ?? ''}
    >
      {label}
    </span>
  );
}
